import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonService } from '../helpers/common.service';
import { AppService } from '../providers/appservice/app.service';
import { OT_DECISIONS, IOTHeatmapHeader, getDecisionImage } from 'src/app/models/input/itdiagnostics/otHeatmap';
import { DashboardService } from '../providers/home/dashboard.service';

const IN_SUPPORT_OPTIONS: any = [
  { name: 'Yes', code: 'yes' },
  { name: 'Extended', code: 'extended' },
  { name: 'Partially', code: 'partially' },
  { name: 'No', code: 'no' }
];

const CONNECTED_OPTIONS: any = [
  { name: 'Fully', code: 'fully' },
  { name: 'Partially', code: 'partially' },
  { name: 'Not Connected', code: 'not connected' }
];

const OPERATIONAL_CRITICALITY: any = [
  { name: 'Very High', code: 'very high' },
  { name: 'High', code: 'high' },
  { name: 'Medium', code: 'medium' },
  { name: 'Low', code: 'low' }
];

const UTILISATION: any = [
  { name: 'High', code: 'high' },
  { name: 'Medium', code: 'medium' },
  { name: 'Low', code: 'low' }
];

const REDUNDANCY: any = [
  { name: 'Full', code: 'full' },
  { name: 'Partial', code: 'partial' },
  { name: 'None', code: 'none' }
];

@Component({
  standalone: false,
  selector: 'app-otheatmp',
  templateUrl: './otheatmp.component.html',
  styleUrls: ['./otheatmp.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OtheatmpComponent implements OnInit {
  otdata: any;
  company: any;
  screen: string;
  selectedObjective: any = {};
  assessmentDimensions: IOTHeatmapHeader[] = [];
  decisions = OT_DECISIONS;
  headerIconBasePath = '/assets/images/uploads/';
  otdataPic: any;

  getDecisionImage = getDecisionImage;

  constructor(
    private config: DynamicDialogConfig,
    private commonService: CommonService,
    public appService: AppService,
    private dashboardService: DashboardService,
    public ref: DynamicDialogRef
  ) { }

  ngOnInit(): void {
    this.screen = this.config?.data?.screen;
    this.selectedObjective = this.config?.data?.selectedObjective || {}
    this.otdata = this.config?.data?.otdata;
    // Get headers from config data (passed from canvas) instead of making API call
    const headers = this.config?.data?.headers || [];
    this.assessmentDimensions = headers.filter((h: IOTHeatmapHeader) => h.key !== 'decision');
    this.commonService.getCompany(this.appService.currentActivatedFeature.strategyId).subscribe(x => {
      this.company = x;
    });
    this.dashboardService.getSelectedImage(this.otdata.pic).subscribe((url: any) => this.otdataPic = url)
  }

  getBgColor(data: any) {
    // If data is null, undefined, or empty, return grey (not assessed)
    if (!data || data === '' || data === 'Not Assessed' || data === 'not assessed') {
      return { 'background-color': '#c3c3c3' }; // Grey
    }

    // Normalize the data to handle case variations
    const normalizedData = typeof data === 'string' ? data.toLowerCase() : '';

    // Severity order: M (Moderate/Amber #FFBF00), R (Red/Significant), G (Green/No Concern)
    // If assessed, show colors based on severity
    switch (normalizedData) {
      case 'severe concern':
        return { 'background-color': '#8d1101' }; // Maroon/Red
      case 'significant concern':
        return { 'background-color': '#d90606' }; // Red (R)
      case 'moderate concern':
        return { 'background-color': '#FFBF00' }; // Amber (M) - #FFBF00 as specified
      case 'no concern':
        return { 'background-color': '#4dc013' }; // Green (G)
      case 'not applicable':
        return { 'background-color': '#ededed' }; // Light grey
      default:
        // If it's assessed but doesn't match known values, check if it has any severity
        // For now, default to grey if not recognized
        return { 'background-color': '#c3c3c3' }; // Grey
    }
  }

  backToObjective() {
    this.ref.close({});
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  formatEnumValue(value: string | undefined, options: any[]): string {
    if (!value || value === '') {
      return '-';
    }
    const option = options.find(opt => opt.code.toLowerCase() === value.toLowerCase());
    return option ? option.name : this.toTitleCase(value);
  }

  formatInSupport(value: string | undefined): string {
    return this.formatEnumValue(value, IN_SUPPORT_OPTIONS);
  }

  formatConnected(value: string | undefined): string {
    return this.formatEnumValue(value, CONNECTED_OPTIONS);
  }

  formatCriticality(value: string | undefined): string {
    return this.formatEnumValue(value, OPERATIONAL_CRITICALITY);
  }

  formatUtilisation(value: string | undefined): string {
    return this.formatEnumValue(value, UTILISATION);
  }

  formatRedundancy(value: string | undefined): string {
    return this.formatEnumValue(value, REDUNDANCY);
  }

  formatDate(dateValue: string | undefined): string {
    if (!dateValue || dateValue === '') {
      return '-';
    }
    // Handle both string dates (MM-DD-YYYY or DD-MM-YYYY) and Date objects
    try {
      // If it's already a formatted string with slashes, return as is
      if (typeof dateValue === 'string' && (dateValue.includes('/') || dateValue.match(/^\d{2}-\d{2}-\d{4}$/))) {
        return dateValue;
      }
      // Try to parse and format if it looks like a date
      const date = new Date(dateValue);
      if (!isNaN(date.getTime()) && dateValue.match(/\d{4}/)) {
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return dateValue; // Return original if it's not a date (e.g., patch level text)
    } catch (e) {
      return dateValue || '-';
    }
  }

  toTitleCase(str: string): string {
    if (!str) return '';
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  onMappingsChange(updatedItem: any): void {
    // Update the otdata with the new mappings
    if (updatedItem && this.otdata) {
      this.otdata = { ...this.otdata, ...updatedItem };
    }
  }
}

